import { Vector3 } from 'three';
import { AudioEngine, type Emitter } from '../audio/engine';
import type { CueId, FloorSound, Loop, WorkKind } from '../audio/cues';
import type { RoundEvent } from '../sim/round';
import type { RoundPhase } from '../sim/types';
import type { Vec3 } from '../sim/types';
import type { InteractionResult } from './interaction';
import type { RadioLine } from './guards';
import { zoneAt, type ZoneDef } from '../world/plan';

/**
 * WHAT THE GAME SOUNDS LIKE, DECIDED HERE.
 *
 * The engine knows how to make a noise at a place; this knows which noise and
 * which place. Keeping those apart matters more than it looks: every decision
 * in this file is a design decision that will be argued with — how loud a
 * sprint is, whether a lockpick is audible across a shop, when the alarm starts
 * — and none of them should require touching a synthesiser to change.
 *
 * The rule it is built around: **sound is information, or it is not in the
 * game.** This is a stealth game whose entire loop is knowing where somebody
 * else is. So a guard's boots are the loudest thing in the mix after the alarm,
 * they get quieter through a wall rather than disappearing, and the player's own
 * footsteps are quiet enough that they never mask them. Ambience is a floor for
 * the silence, not a soundscape. Nothing plays because a AAA game would have
 * played something there.
 */

/**
 * Build one, or don't.
 *
 * Web Audio is the one browser API this project uses that can be absent — under
 * vitest's node environment there is no `AudioContext` at all — and the game has
 * to keep running without it rather than fail to construct. Every call site
 * therefore holds a nullable director and reaches through `?.`, which is a
 * small, honest tax for a whole subsystem that is allowed to not exist.
 */
export function tryDirector(opts: DirectorOptions): AudioDirector | null {
  try {
    if (typeof window === 'undefined') return null;
    const w = window as unknown as { AudioContext?: unknown; webkitAudioContext?: unknown };
    if (!w.AudioContext && !w.webkitAudioContext) return null;
    return new AudioDirector(opts);
  } catch {
    return null;
  }
}

/** Everything the director needs to know this frame. Assembled by the session. */
export interface AudioFrame {
  dt: number;
  /** Camera position and orientation. The listener, literally. */
  eye: Vector3;
  forward: Vector3;
  up: Vector3;
  /** The player's feet, which is where their own footsteps happen. */
  feet: Vec3;
  travelled: number;
  grounded: boolean;
  crouching: boolean;
  speed: number;
  phase: RoundPhase;
  clock: number;
  detection: number;
  paused: boolean;
  /** Null unless the interact key is down on something that makes a noise. */
  work: WorkKind | null;
  workProgress: number;
  guards: ReadonlyArray<{ id: string; pos: Vec3; speed: number; mode: string }>;
}

export interface DirectorOptions {
  zones: ZoneDef[];
  /** True when nothing solid stands between these two points. */
  clear(from: Vec3, to: Vec3): boolean;
  /**
   * The thing that actually makes noise. Injectable, and the reason is tests.
   *
   * Every interesting decision in this file — how far a thief walks between
   * footsteps, when the sting is allowed to fire again, whether a guard who has
   * stopped to scan goes quiet, how far ahead the alarm is queued — is a number
   * that will be wrong at some point and has to be provable. None of them
   * involve a synthesiser. Depending on the narrow interface below rather than
   * on `AudioEngine` means the whole director runs under vitest against a fake
   * that records calls, so those numbers are checked by a test rather than by
   * me claiming to have listened to them.
   */
  engine?: SoundOut;
}

/**
 * The slice of the audio engine the director actually uses.
 *
 * Deliberately written from the call sites rather than derived from the class,
 * so that widening it is a visible edit and the director cannot quietly grow a
 * dependency on the graph underneath it.
 */
export interface SoundOut {
  readonly now: number;
  readonly running: boolean;
  /**
   * The graph itself, for voice and nothing else.
   *
   * Optional because the fake this interface exists for has no graph, and
   * because a platform with no Web Audio has to keep working. Voice is the one
   * feature that has to build its own nodes — a decoder's output is not a cue
   * and cannot go through `play` — so it is the one thing allowed to reach past
   * the director for the context everything else is scheduled on.
   */
  readonly ctx?: AudioContext;
  emitter(key: string): Emitter;
  listener(pos: Vector3, forward: Vector3, up: Vector3): void;
  update(dt: number): void;
  play(id: CueId, gain?: number, at?: number): void;
  playAt(id: CueId, x: number, y: number, z: number, occlusion?: number, gain?: number): void;
  playOn(em: Emitter, id: CueId, gain?: number): void;
  startWork(kind: WorkKind): Loop | null;
  startBed(): void;
  setBed(back: number): void;
  duck(amount: number, seconds?: number): void;
}

/**
 * Stride length in metres. Longer at speed, because a sprint is not a walk
 * played faster — a runner covers more ground per footfall, and a footstep rate
 * that scales linearly with velocity is the single most common mistake in game
 * audio and sounds like a sewing machine.
 */
function stride(speed: number, crouching: boolean): number {
  if (crouching) return 0.62;
  return 0.72 + Math.min(0.55, speed * 0.075);
}

/** Where the ear sits relative to the panner, so nothing is ever exactly at 0. */
const EAR = 1.55;
/** How often an emitter re-tests whether a wall is in the way, in seconds. */
const OCCLUSION_EVERY = 0.2;
/** Awareness at which the sting fires, and the level it re-arms below. */
const STING_AT = 0.3;
const STING_REARM = 0.1;

interface GuardTrack {
  em: Emitter;
  last: Vector3;
  gait: number;
  occludeIn: number;
  mode: string;
}

export class AudioDirector {
  readonly engine: SoundOut;

  private readonly zones: ZoneDef[];
  private readonly clear: (from: Vec3, to: Vec3) => boolean;
  private readonly tracks = new Map<string, GuardTrack>();
  private readonly earAt = { x: 0, y: 0, z: 0 };
  private readonly scratch = new Vector3();

  private gait = 0;
  private airborne = 0;
  private wasGrounded = true;
  private phase: RoundPhase | null = null;
  private stingArmed = true;
  private bedBack = 0;
  private lastTick = -1;
  /** Context time the next alarm cycle is already scheduled for. */
  private alarmUntil = 0;
  private alarmed = false;
  private workLoop: Loop | null = null;
  private workKind: WorkKind | null = null;

  constructor(opts: DirectorOptions) {
    this.zones = opts.zones;
    this.clear = opts.clear;
    this.engine = opts.engine ?? new AudioEngine();
  }

  /** The one call the session makes every frame. */
  frame(f: AudioFrame): void {
    const e = this.engine;
    if (!e.running) return;
    e.startBed();
    e.listener(f.eye, f.forward, f.up);
    e.update(f.dt);
    e.duck(f.paused ? 0.55 : 0);

    this.earAt.x = f.eye.x;
    this.earAt.y = f.eye.y;
    this.earAt.z = f.eye.z;

    this.room(f);
    this.feet(f);
    this.hands(f);
    this.shift(f);
    this.tension(f);
    this.clockwork(f);
  }

  // -- the player -----------------------------------------------------------

  /**
   * Footsteps, from distance travelled rather than from a timer.
   *
   * Tying the gait to metres covered means it is automatically right for every
   * speed the game can produce — walking, sprinting, crouch-creeping, and the
   * slow grind of being sixty kilos over the weight limit — without a single
   * speed threshold anywhere. Walking into a wall stops the footsteps, which a
   * timer would not, and that is not a detail: a player pressed against a wall
   * making noise they did not ask for is a player who mistrusts the audio.
   */
  private feet(f: AudioFrame): void {
    if (f.grounded && !this.wasGrounded) {
      if (this.airborne > 0.22) this.engine.play('land', 0.7);
      this.airborne = 0;
      this.gait = 0;
    }
    if (!f.grounded) {
      if (this.wasGrounded) this.engine.play('cloth', 0.8);
      this.airborne += f.dt;
      this.wasGrounded = false;
      return;
    }
    this.wasGrounded = true;
    if (f.travelled <= 0) return;

    this.gait += f.travelled;
    const len = stride(f.speed, f.crouching);
    if (this.gait < len) return;
    this.gait -= len;

    const surface = this.floorAt(f.feet);
    // Own footsteps are mixed low on purpose. They are the sound the player has
    // the least need to hear and the most exposure to, and every decibel they
    // take is a decibel of headroom the guard walking past the door does not get.
    const effort = f.crouching ? 0.16 : Math.min(1, 0.28 + f.speed * 0.11);
    this.engine.play(
      surface === 'concrete' ? 'stepConcrete' : surface === 'asphalt' ? 'stepAsphalt' : f.crouching ? 'stepCreep' : 'step',
      0.42 * (0.5 + effort * 0.5),
    );
  }

  private hands(f: AudioFrame): void {
    const want = f.work && f.work !== 'none' && f.work !== 'take' && f.work !== 'leave' ? f.work : null;
    if (want !== this.workKind) {
      this.workLoop?.stop(this.engine.now);
      this.workLoop = want ? this.engine.startWork(want) : null;
      this.workKind = want;
    }
    this.workLoop?.set(f.workProgress);
  }

  // -- everyone else --------------------------------------------------------

  /**
   * Guard footsteps, and the reason this whole system exists.
   *
   * Each guard has a permanent emitter that follows them, and their gait is
   * integrated from how far they actually moved — so a guard who has stopped to
   * scan a room goes silent, which is exactly the information a thief crouched
   * behind a shelf needs and exactly what a looping "guard walking" sound would
   * destroy. The occlusion ray runs five times a second per guard, which at
   * three guards is fifteen raycasts a second against a world the physics engine
   * is already stepping sixty times.
   */
  private shift(f: AudioFrame): void {
    for (const g of f.guards) {
      let t = this.tracks.get(g.id);
      if (!t) {
        t = {
          em: this.engine.emitter(g.id),
          last: new Vector3(g.pos.x, g.pos.y, g.pos.z),
          gait: 0,
          occludeIn: 0,
          mode: g.mode,
        };
        this.tracks.set(g.id, t);
      }
      t.em.set(g.pos.x, g.pos.y + 0.9, g.pos.z);

      t.occludeIn -= f.dt;
      if (t.occludeIn <= 0) {
        t.occludeIn = OCCLUSION_EVERY;
        // Ear height on both ends. A ray from the camera to a guard's boots
        // clips the floor at any distance and reports every guard in the
        // building as occluded.
        t.em.occlusion = this.clear(this.earAt, { x: g.pos.x, y: g.pos.y + EAR, z: g.pos.z }) ? 0 : 1;
      }

      this.scratch.set(g.pos.x, g.pos.y, g.pos.z);
      const moved = this.scratch.distanceTo(t.last);
      t.last.copy(this.scratch);
      // Ignore the metre-per-frame jumps a restart or a nav teleport produces.
      if (moved > 0 && moved < 0.6) {
        t.gait += moved;
        const len = stride(g.speed, false) * 1.08;
        if (t.gait >= len) {
          t.gait -= len;
          this.engine.playOn(t.em, 'stepGuard', 0.85);
        }
      }

      // A guard breaking into a run is worth hearing even through a wall.
      if (t.mode !== g.mode) {
        if (g.mode === 'chase' && t.mode !== 'chase') this.engine.playOn(t.em, 'cloth', 1.2);
        t.mode = g.mode;
      }
    }
  }

  /** A radio call, from wherever the guard who made it is standing. */
  radio(lines: readonly RadioLine[]): void {
    for (const l of lines) {
      const t = this.tracks.get(l.id);
      if (t) this.engine.playOn(t.em, 'radio', 1);
      else this.engine.playAt('radio', l.at.x, l.at.y + EAR, l.at.z);
    }
  }

  // -- the state of things --------------------------------------------------

  /**
   * The sting, and the alarm.
   *
   * Two different jobs. The sting fires once, the instant a guard's awareness
   * crosses from "something moved" into "there is a person there", and its whole
   * purpose is to tell a player looking the wrong way that they have about a
   * second to decide something. It re-arms only after awareness has properly
   * decayed, so a thief edging in and out of a sight line does not get pinged
   * eleven times.
   *
   * The alarm is the building's reaction, not a guard's: it starts when someone
   * is actually being chased and runs until nobody is. It is deliberately not
   * spatialised — it is coming from every ceiling in the mall — and it is the
   * one sound allowed to make the rest of the mix harder to hear, because a
   * chase where you can still calmly listen for footsteps is not a chase.
   */
  private tension(f: AudioFrame): void {
    if (this.stingArmed && f.detection >= STING_AT) {
      this.engine.play('spotted', 0.9);
      this.stingArmed = false;
    } else if (!this.stingArmed && f.detection < STING_REARM) {
      this.stingArmed = true;
    }

    const hunting = f.guards.some((g) => g.mode === 'chase' || g.mode === 'search');
    this.alarmed = hunting && f.phase === 'active';
    if (!this.alarmed) return;
    // Scheduled a cycle ahead so the two-tone never gaps. Web Audio will happily
    // take the whole sequence in advance; what it will not do is stitch two
    // cycles together if the second is queued after the first has already ended.
    const now = this.engine.now;
    if (this.alarmUntil < now) this.alarmUntil = now;
    while (this.alarmUntil < now + 1.2) {
      this.engine.play('alarm', 0.55, this.alarmUntil - now);
      this.alarmUntil += 1.0;
    }
  }

  /** The clock, once it starts mattering. */
  private clockwork(f: AudioFrame): void {
    if (f.phase !== this.phase) {
      if (f.phase === 'active') this.engine.play('doorsOpen', 0.9);
      if (f.phase === 'results') {
        this.engine.play('roundEnd', 0.9);
        this.workLoop?.stop(this.engine.now);
        this.workLoop = null;
        this.workKind = null;
      }
      this.phase = f.phase;
      this.lastTick = -1;
    }
    if (f.phase !== 'active') return;
    const left = Math.ceil(f.clock);
    if (left > 60 || left === this.lastTick || left <= 0) return;
    this.lastTick = left;
    this.engine.play(left <= 10 ? 'tickUrgent' : 'tick', left <= 10 ? 0.9 : 0.55);
  }

  /**
   * Which room the bed is playing.
   *
   * Read off the floor rather than off a zone list, because the floor already
   * encodes the answer: the shop floor is tile and the back of house is bare
   * concrete and asphalt. Eased over about a second, so a doorway is a
   * transition rather than an edit.
   */
  private room(f: AudioFrame): void {
    const surface = this.floorAt(f.feet);
    const want = surface === 'concrete' || surface === 'asphalt' ? 1 : 0;
    this.bedBack += (want - this.bedBack) * Math.min(1, f.dt * 1.1);
    this.engine.setBed(this.bedBack);
  }

  private floorAt(p: Vec3): FloorSound {
    return (zoneAt(this.zones, p.x, p.y, p.z)?.floor ?? 'tile') as FloorSound;
  }

  // -- one-off things that happened -----------------------------------------

  /** Called with every round event the session drains. */
  event(e: RoundEvent, guardAt?: Vec3): void {
    switch (e.t) {
      case 'capture':
        this.engine.play('caught', 1);
        break;
      case 'guardDown':
        if (guardAt) this.engine.playAt('guardDown', guardAt.x, guardAt.y + 0.4, guardAt.z);
        else this.engine.play('guardDown', 0.8);
        break;
      case 'deposit':
        // Scaled by the haul, so a nine-thousand-dollar drop and a two-hundred
        // dollar drop are not the same event.
        this.engine.play('bank', Math.min(1.3, 0.55 + Math.log10(Math.max(10, e.amount)) * 0.2));
        break;
      default:
        break;
    }
  }

  /** Called with the result of a completed interaction. */
  outcome(r: InteractionResult, at: Vec3): void {
    switch (r.kind) {
      case 'refused':
        this.engine.play('refused', 0.8);
        break;
      case 'breached':
        this.engine.playAt('breach', at.x, at.y, at.z);
        // A shutter is a different, much bigger event than a cabinet lock, and
        // the difference is the point: one is heard across the atrium.
        if (r.lock === 'cut' || r.lock === 'force') this.engine.playAt('shutter', at.x, at.y, at.z, 0, 0.75);
        break;
      case 'looted':
        this.engine.play('take', 0.85);
        break;
      case 'deposited':
        this.engine.playAt('chute', at.x, at.y, at.z, 0, 0.9);
        break;
    }
  }

  /** The thief shoved someone. */
  shove(at: Vec3, landed: boolean): void {
    if (landed) this.engine.playAt('shove', at.x, at.y + 1.0, at.z);
    else this.engine.play('refused', 0.6);
  }

  /** Something was dropped on the floor in front of the player. */
  dropped(at: Vec3): void {
    this.engine.playAt('drop', at.x, at.y + 0.2, at.z, 0, 0.8);
  }

  /** A new round. Everything that was running has to stop running. */
  reset(): void {
    this.workLoop?.stop(this.engine.now);
    this.workLoop = null;
    this.workKind = null;
    this.stingArmed = true;
    this.lastTick = -1;
    this.phase = null;
    this.gait = 0;
    for (const t of this.tracks.values()) t.gait = 0;
  }
}
